import { t } from 'i18next'
import { storeToRefs } from 'pinia'
import { type Ref, ref } from 'vue'

import {
  isBoundDeviceCategory,
  isBoundDeviceState,
  isProductStateWithSelectedProduct,
  type ProductSetGroup,
  type StackItem,
} from '@/components/newProductPanel/newProductPanel.type'
import {
  type BoundDeviceCategoryFieldsFragment,
  StockStatus,
} from '@/graphql/creator-platform/generated'
import { useNewProductStore } from '@/store/newProductStore'

export function isSingleProduct(productSetGroup: ProductSetGroup): boolean {
  return (
    productSetGroup.productSets.length === 1 &&
    productSetGroup.productSets[0]?.productOption.length === 1
  )
}

export function getStockStatusTagClasses(stockStatus: StockStatus): string {
  switch (stockStatus) {
    case StockStatus.ForSale: {
      return 'hidden'
    }
    case StockStatus.Pending: {
      return 'bg-primary-10 text-primary-50'
    }
    case StockStatus.PreOrder: {
      return 'bg-red bg-opacity-10 text-red'
    }
  }
}

interface ProductSelector {
  changeProduct: (productHandle: string) => void
  expandedProductSet: Ref<null | string>
  selectBrand: () => void
  selectDeviceColor: () => void
  selectProductSetGroup: () => void
  selectVariant: () => void
  stackMenu: Ref<StackItem[]>
}

export function useProductSelector(onReset: () => void): ProductSelector {
  const { catalog, productState } = storeToRefs(useNewProductStore())
  const newProductStore = useNewProductStore()
  const stackMenu = ref<StackItem[]>([])
  const expandedProductSet = ref<null | string>(null)

  function selectBrand(): void {
    if (catalog.value === null) return
    const type = productState.value?.categoryType
    const selectedCategory = catalog.value.find((e) => {
      return e.__typename === 'BoundDeviceCategoryDto' && e.boundDeviceType === type
    })
    if (!isBoundDeviceCategory(selectedCategory) || type === undefined) return
    const options = selectedCategory.brands.map((e) => {
      return { value: e.name, name: e.name }
    })
    // skip brand selection if there's only 1 brand
    if (selectedCategory.brands.length === 1) {
      selectDevice(
        selectedCategory.brands[0]?.name ?? '',
        selectedCategory,
        t(`catalog.${type}.device`),
      )
      return
    }
    stackMenu.value.push({
      options,
      title: t(`catalog.${type}.device`),
      onClick: (e) => {
        selectDevice(e ?? '', selectedCategory)
      },
      type: 'expandableItem',
    })
  }

  function selectDevice(
    brand: string,
    category: BoundDeviceCategoryFieldsFragment,
    title?: string,
  ): void {
    const focusBrand = category.brands.find((e) => e.name === brand)
    if (focusBrand === undefined) return
    const options = focusBrand.devices.map((e) => {
      return {
        name: e.name,
        value: e.handle,
      }
    })
    stackMenu.value.push({
      options,
      title: title ?? brand,
      onClick: (e) => {
        if (e === undefined) return
        void newProductStore.changeDevice(e)
        onReset()
      },
      type: 'standaloneItem',
    })
  }

  function selectDeviceColor(): void {
    if (productState.value === null) return
    const type = productState.value.categoryType
    if (!isBoundDeviceState(productState.value)) return
    const options = productState.value.device.colors.map((e) => {
      return { name: e.name, value: e.handle, colorType: e.type, colorDisplay: e.value }
    })
    stackMenu.value.push({
      options,
      title: t(`catalog.${type}.device-color`),
      onClick: (e) => {
        if (e === undefined) return
        void newProductStore.changeDeviceColor(e)
        onReset()
      },
      type: 'color',
    })
  }

  function selectProductSetGroup(): void {
    if (productState.value === null || !isProductStateWithSelectedProduct(productState.value))
      return
    const productSetGroups = productState.value.selectedProduct.productSetGroups.map((e) => {
      const isProductUnavailable = e.productSets.every(
        (productSet) => productSet.products.length === 0,
      )
      return {
        productSetGroup: {
          productSets: isProductUnavailable
            ? []
            : e.productSets.map((productSet) => ({
                productOption: productSet.products.map((product) => ({
                  version: product.version,
                  handle: product.handle,
                })),
                designGroupType: productSet.boundDesignGroup.type,
                handle: productSet.handle,
              })),
          groupKey: e.groupKey,
        },
        thumbnail: e.thumbnailUrl,
      }
    })
    expandedProductSet.value = productState.value.selectedProduct.productSetGroup.groupKey
    stackMenu.value.push({
      productSetGroups,
      title: t(`catalog.${productState.value.categoryType}.product`),
    })
  }

  function changeProduct(productHandle: string): void {
    void newProductStore.changeProduct(productHandle)
    onReset()
  }

  function selectVariant(): void {
    if (productState.value === null) return
    const options = productState.value.selectedProduct?.product.variants.map((e) => {
      return {
        name: e.color.name,
        value: e.sku,
        colorDisplay: e.color.value,
        colorType: e.color.type,
        stockStatus: e.stockStatus,
      }
    })
    if (options === undefined) return
    stackMenu.value.push({
      onClick: (e) => {
        if (e === undefined) return
        void newProductStore.changeProductVariant(e)
        onReset()
      },
      options,
      title: t(`catalog.${productState.value.categoryType}.product-color`),
      type: 'color',
    })
  }

  return {
    expandedProductSet,
    changeProduct,
    selectProductSetGroup,
    selectVariant,
    selectDeviceColor,
    selectBrand,
    stackMenu,
  }
}
